import React from "react";
import { Box, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import NumberFormat from "react-number-format";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IParametro } from "../../redux/types/parametroActionTypes";
import { currencyFormat, getValorParametro } from "../../blocks/utility";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";
import {
    descripcionTipoUbicacion,
    esModalidadNormal,
    esOcurre,
    etiquetaModalidadActiva,
    iconoModalidadFecha,
    iconoTipoRecoleccion,
    lineasHorariosAreaServicio,
    textoFechaModalidadRecoleccion,
    textoFormaRecoleccionEntrega,
    textoSucursalOcurre
} from "./revisionGuiaResumenHelpers";

const useRecoleccionStyles = makeStyles(() =>
    createStyles({
        card: {
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 4,
            padding: "12px 14px",
            backgroundColor: "#fafafa",
            marginTop: 8
        },
        iconRow: {
            display: "flex",
            alignItems: "flex-start"
        },
        icon: {
            width: 36,
            marginRight: 12,
            flexShrink: 0
        },
        iconSmall: {
            width: 18,
            marginRight: 6,
            flexShrink: 0
        },
        titulo: {
            color: "#19754e",
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: 1.4
        },
        secundario: {
            fontSize: "13px",
            color: "rgba(0,0,0,0.65)",
            lineHeight: 1.4,
            marginTop: 2
        },
        modalidad: {
            fontSize: "13px",
            fontWeight: 600,
            color: "rgba(0,0,0,0.87)",
            marginTop: 6
        },
        detalle: {
            fontSize: "13px",
            color: "rgba(0,0,0,0.87)",
            lineHeight: 1.5,
            marginTop: 2
        },
        horario: {
            fontSize: "13px",
            fontWeight: 600,
            color: "rgba(0,0,0,0.87)"
        }
    })
);

interface ResumenRecoleccionReadOnlyProps {
    rec: IRecoleccion;
    esOrigen: boolean;
}

export const ResumenRecoleccionReadOnly = ({ rec, esOrigen }: ResumenRecoleccionReadOnlyProps) => {
    const classes = useRecoleccionStyles();
    const iconoUbicacion = iconoTipoRecoleccion(rec.tipoRecoleccionId);
    const iconoFecha = iconoModalidadFecha(rec, esOrigen);
    const forma = textoFormaRecoleccionEntrega(rec, esOrigen);
    const descripcion = descripcionTipoUbicacion(rec, esOrigen);
    const fechaDetalle = textoFechaModalidadRecoleccion(rec, esOrigen);
    const sucursal = textoSucursalOcurre(rec);
    const horarios = lineasHorariosAreaServicio(rec);
    const modalidadLabel = etiquetaModalidadActiva(rec, esOrigen);
    const normal = esModalidadNormal(rec, esOrigen);
    const ocurre = esOcurre(rec);

    return (
        <Box className={classes.card}>
            <Box className={classes.iconRow}>
                {iconoUbicacion && (
                    <img src={`/image/${iconoUbicacion}`} alt="" className={classes.icon} />
                )}
                <Box flex={1}>
                    <Typography className={classes.titulo}>{forma}</Typography>
                    {descripcion && (
                        <Typography className={classes.secundario}>{descripcion}</Typography>
                    )}
                </Box>
            </Box>

            {ocurre ? (
                <>
                    {sucursal && (
                        <Typography className={classes.detalle} style={{ marginTop: 8 }}>
                            <strong>{traducirLabel(288)}:</strong> {sucursal}
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    <Typography className={classes.modalidad}>{modalidadLabel}</Typography>
                    {normal && horarios.length > 0 && (
                        <Box mt={0.5}>
                            {horarios.map((h, i) => (
                                <Typography key={i} className={classes.horario}>{h}</Typography>
                            ))}
                        </Box>
                    )}
                    {normal && esOrigen && !fechaDetalle && (
                        <Typography className={classes.secundario} style={{ marginTop: 4 }}>
                            {traducirMessage(122)}
                        </Typography>
                    )}
                    {fechaDetalle && (
                        <Box display="flex" alignItems="center" mt={0.5}>
                            {iconoFecha && (
                                <img src={`/image/${iconoFecha}`} alt="" className={classes.iconSmall} />
                            )}
                            <Typography className={classes.detalle}>{fechaDetalle}</Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

const useSeguroStyles = makeStyles(() =>
    createStyles({
        card: {
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 4,
            padding: "12px 16px",
            backgroundColor: "#fafafa",
            maxWidth: 280
        },
        titulo: {
            color: "#19754e",
            fontWeight: 700,
            fontSize: "15px",
            textAlign: "center",
            marginBottom: 8
        },
        linea: {
            fontSize: "13px",
            color: "rgba(0,0,0,0.87)",
            lineHeight: 1.5
        },
        costoRow: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginTop: 8,
            fontSize: "13px"
        },
        costoValor: {
            fontWeight: 700
        }
    })
);

interface ResumenSeguroReadOnlyProps {
    guiaWizard: IGuiaWizard;
    parametros: IParametro[];
    costoTarifa: number;
}

export const ResumenSeguroReadOnly = ({ guiaWizard, parametros, costoTarifa }: ResumenSeguroReadOnlyProps) => {
    const classes = useSeguroStyles();
    const coberturaBasico = getValorParametro(parametros, "seguroBasicoCubre");
    const coberturaDeclaradoMax = getValorParametro(parametros, "seguroDeclaradoMaximo");
    const label40Partes = traducirLabel(40).split("#####");

    if (guiaWizard.esSeguroBasico) {
        return (
            <Box className={classes.card}>
                <Typography className={classes.titulo}>{traducirLabel(273)}</Typography>
                <Typography className={classes.linea}>
                    {label40Partes[0]}
                    <NumberFormat
                        value={coberturaBasico}
                        displayType="text"
                        prefix="$"
                        thousandSeparator
                        style={{ padding: "0 4px" }}
                    />
                    {label40Partes[1]}
                </Typography>
                <Box className={classes.costoRow}>
                    <span>{traducirLabel(57)}</span>
                    <span className={classes.costoValor}>{currencyFormat(costoTarifa)}</span>
                </Box>
            </Box>
        );
    }

    if (guiaWizard.esSeguroMercancia) {
        return (
            <Box className={classes.card}>
                <Typography className={classes.titulo}>{traducirLabel(274)}</Typography>
                <Typography className={classes.linea}>
                    {label40Partes[0]}
                    <NumberFormat
                        value={coberturaDeclaradoMax}
                        displayType="text"
                        prefix="$"
                        thousandSeparator
                        style={{ padding: "0 4px" }}
                    />
                    {label40Partes[1]}
                </Typography>
                {guiaWizard.valorMercancia != null && (
                    <Typography className={classes.linea} style={{ marginTop: 6 }}>
                        {traducirLabel(314)}: {currencyFormat(Number(guiaWizard.valorMercancia))}
                    </Typography>
                )}
                <Box className={classes.costoRow}>
                    <span>{traducirLabel(57)}</span>
                    <span className={classes.costoValor}>{currencyFormat(costoTarifa)}</span>
                </Box>
            </Box>
        );
    }

    return null;
};
